import { Injectable } from '@angular/core';

export const CSS_VALUE_EXPR_PREFIX = 'cssvalue:';
export const CSS_DECLARATION_EXPR_PREFIX = 'cssdecl:';

const DATA_TPL_MARKER = 'data-tpl-';
const STYLE_ATTR_NAME = 'style';
const SCALAR_TOKEN_START = '[[';
const CSS_EXPR_PREFIXES: readonly string[] = [CSS_VALUE_EXPR_PREFIX, CSS_DECLARATION_EXPR_PREFIX];
const STYLE_ATTR_PATTERN = /(^|\s)style=(["'])([\s\S]*?)\2/g;
const SCALAR_EXPR_PATTERN = /\[\[(?![#/])([^\]]+)\]\]/g;
const ATTR_EXPR_PATTERN = /\bdata-tpl-attr-([a-z][a-z0-9-]*)="([^"]*)"/g;
const STYLE_EXPR_TAG_PATTERN = /<([a-zA-Z][a-zA-Z0-9-]*)([^>]*?)\sdata-tpl-style="([^"]*)"([^>]*?)>/g;

@Injectable({ providedIn: 'root' })
export class TemplateStructurePreprocessorService {
    expandStructuralAttributes(html: string): string {
        const marked = this.markStyleExpressions(html);
        if (!marked.includes(DATA_TPL_MARKER)) return marked;

        let result = this.mergeDynamicStyles(marked);
        result = result.replace(ATTR_EXPR_PATTERN, (_, attrName: string, expr: string) =>
            `${attrName}="[[${attrName === STYLE_ATTR_NAME ? CSS_DECLARATION_EXPR_PREFIX : ''}${expr}]]"`
        );
        result = this.expandUntilStable(result, 'each');
        result = this.expandUntilStable(result, 'if');
        return result;
    }

    private markStyleExpressions(html: string): string {
        if (!html.includes(SCALAR_TOKEN_START)) return html;
        return html.replace(STYLE_ATTR_PATTERN, (_, lead: string, quote: string, value: string) =>
            `${lead}style=${quote}${this.markCssExpressions(value)}${quote}`
        );
    }

    private markCssExpressions(styleValue: string): string {
        if (!styleValue.includes(SCALAR_TOKEN_START)) return styleValue;
        return styleValue.replace(SCALAR_EXPR_PATTERN, (token, expr: string, offset: number) => {
            const trimmed = expr.trim();
            if (CSS_EXPR_PREFIXES.some(prefix => trimmed.startsWith(prefix))) return token;
            const prefix = this.isCssValuePosition(styleValue, offset) ? CSS_VALUE_EXPR_PREFIX : CSS_DECLARATION_EXPR_PREFIX;
            return `[[${prefix}${trimmed}]]`;
        });
    }

    private isCssValuePosition(styleValue: string, tokenOffset: number): boolean {
        const declaration = styleValue.slice(0, tokenOffset).split(';').pop() ?? '';
        return declaration.includes(':');
    }

    // Dynamic styles cannot ride inside `style=""` because CKEditor parses style as CSS and drops
    // any [[expr]] token as an invalid declaration. They travel on a preserved `data-tpl-style`
    // attribute (a CSS fragment that may contain [[expr]] tokens — CKEditor keeps data-* values
    // verbatim) and are appended back into style, so they win over the static declarations.
    private mergeDynamicStyles(html: string): string {
        return html.replace(STYLE_EXPR_TAG_PATTERN, (_, tag: string, before: string, fragment: string, after: string) => {
            const styleRx = /(^|\s)style="([^"]*)"/;
            let attrs = `${before} ${after}`;
            attrs = styleRx.test(attrs)
                ? attrs.replace(styleRx, (_m, lead: string, existing: string) => `${lead}style="${this.markCssExpressions(this.joinStyles(existing, fragment))}"`)
                : `${attrs} style="${this.markCssExpressions(fragment)}"`;
            attrs = attrs.replace(/\s{2,}/g, ' ').replace(/^\s+|\s+$/g, '');
            return `<${tag} ${attrs}>`;
        });
    }

    private joinStyles(existing: string, fragment: string): string {
        const base = existing.replace(/;?\s*$/, '');
        return base ? `${base};${fragment}` : fragment;
    }

    private expandUntilStable(html: string, directive: 'each' | 'if'): string {
        let current = html;
        let prev: string;
        do {
            prev = current;
            current = this.expandDirective(current, directive);
        } while (current !== prev);
        return current;
    }

    private expandDirective(html: string, directive: 'each' | 'if'): string {
        const attrName = `data-tpl-${directive}`;
        const openToken = (expr: string) => `[[#${directive} ${expr}]]`;
        const closeToken = `[[/${directive}]]`;

        const openTagRx = new RegExp(
            `<([a-zA-Z][a-zA-Z0-9-]*)([^>]*)\\s${attrName}="([^"]*)"([^>]*)>`,
            'g'
        );

        const parts: string[] = [];
        let lastIndex = 0;
        let m: RegExpExecArray | null;
        openTagRx.lastIndex = 0;

        while ((m = openTagRx.exec(html)) !== null) {
            const tagName = m[1];
            const before = m[2];
            const expr = m[3];
            const after = m[4];
            const matchStart = m.index;
            const matchEnd = m.index + m[0].length;

            const otherAttrs = (before + after).replace(/^\s+|\s+$/g, '');
            const reopenedTag = otherAttrs ? `<${tagName} ${otherAttrs}>` : `<${tagName}>`;

            const rest = html.slice(matchEnd);
            const closeRx = new RegExp(`</${tagName}>`, 'i');
            const closeM = closeRx.exec(rest);

            if (!closeM) {
                parts.push(html.slice(lastIndex, matchEnd));
                lastIndex = matchEnd;
                openTagRx.lastIndex = lastIndex;
                continue;
            }

            const innerContent = rest.slice(0, closeM.index);
            const afterClose = matchEnd + closeM.index + closeM[0].length;

            parts.push(html.slice(lastIndex, matchStart));
            parts.push(`${openToken(expr)}${reopenedTag}${innerContent}</${tagName}>${closeToken}`);
            lastIndex = afterClose;
            openTagRx.lastIndex = lastIndex;
        }

        parts.push(html.slice(lastIndex));
        return parts.join('');
    }
}
